import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './SortSelector.styles';
import { BottomSheetSelect } from './BottomSheetSelect';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdOrder } from '@/api/types/ads.types';
import { SortIcon } from '@/components/UI/Icons/Icons';
import { useResponsive } from '@/hooks/useResponsive';
import { SortDropdown } from '@/components/Filters/SortDropdown';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

interface Props {
  value?: AdOrder;
  onChange: (value: AdOrder) => void;
  dataTestId?: string;
  isLoading?: boolean;
}

export const SortSelector = ({
  value = AdOrder.DateDesc,
  onChange,
  dataTestId,
  isLoading,
}: Props) => {
  const [isOpen, toggle] = useToggleOpenState();
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const {
    query: { order },
  } = useRouter();

  const options = Object.values(AdOrder).filter(el => el !== AdOrder.Random);
  const selected = order
    ? t(`common.${value.toLowerCase()}`)
    : t('common.sorting');

  function handleChangeOrder(newValue: AdOrder) {
    onChange(newValue);
    toggle(false);
  }

  if (isLoading) {
    return isDesktop ? (
      <SkeletonLoader height={40} width={173} borderRadius={20} />
    ) : (
      <SkeletonLoader height={17} width={75} borderRadius={7} />
    );
  }

  if (isDesktop) {
    return (
      <S.DesktopSortButton
        $isActive={isOpen}
        onClick={toggle}
        data-testid={dataTestId}
      >
        <SortIcon width={14.4} height={12} />
        {selected}
        <S.DropdownIcon $isActive={isOpen} />
        <SortDropdown isOpen={isOpen} onClose={() => toggle(false)}>
          <S.Menu>
            {options.map(item => (
              <S.MenuItem
                key={item}
                id={
                  item !== AdOrder.DateDesc
                    ? `order_${item.toLowerCase()}`
                    : undefined
                }
                $isActive={item === value}
                data-testid="sort-item"
                onClick={() => handleChangeOrder(item)}
              >
                <S.MenuItemText>
                  {t(`common.${item.toLowerCase()}`)}
                </S.MenuItemText>
                <S.RadioButton $isActive={item === value} />
              </S.MenuItem>
            ))}
          </S.Menu>
        </SortDropdown>
      </S.DesktopSortButton>
    );
  }

  return (
    <>
      <S.SortButton
        appearance="transparent"
        onClick={toggle}
        data-testid={dataTestId}
      >
        <SortIcon width={16} height={16} />
        {selected}
      </S.SortButton>
      <BottomSheetSelect
        dataTestId={SearchResultsPageTestIds.inlineFilters.sortBottomSheet}
        title={t('common.sorting')}
        isOpen={isOpen}
        close={() => toggle(false)}
        options={options.map(item => ({
          dataTestId: `sort-item`,
          id:
            item !== AdOrder.DateDesc
              ? `order_${item.toLowerCase()}`
              : undefined,
          label: t(`common.${item.toLowerCase()}`),
          isSelected: item === value,
          onSelect: () => onChange(item),
        }))}
      />
    </>
  );
};
